import { type z, type ZodMiniObject } from 'zod/v4-mini';

import type { OpzlyExperiments, OpzlyExperimentName } from './config';

type Weighting = number;
type Identifier = string;

export type Experiment = {
  name: string;
  variants: [Weighting, Identifier][];
  parentExperiment?: {
    name: string;
    /**
     * If the user is in this of parent experiment they will
     * be forced into the control group of the child experiment
     */
    controlConditionGroup: string | ((arg: string) => string);
  };
  winningVariant?: null | string;
};

export type ActiveExperiment = {
  variant: string;
} & Partial<Experiment>;

export type ActiveExperiments = ActiveExperiment[];

export interface CurrentExperimentConfig {
  [key: string]: Experiment;
}

/**
 * Types for JIT/Optimizely underneath this line
 */

/**
 * These types are what we send to the backend for
 * both bucketing and preview calls
 */
export type OpzlyExperimentFlag = {
  name: string;
  override?: string;
};

/**
 * Cached* types define what we store in the RQ cache
 */
export type CachedVariant = {
  decision: string;
  bucketed: boolean;
  variables?: Record<string, string | boolean | number>;
};

export type CachedExperiments = Record<string, CachedVariant>;

export const OPTIMIZELY_EXPERIMENT_CONTROL = 'control' as const;
export const OPTIMIZELY_EXPERIMENT_OPTIONS = [
  'variant',
  'variantA',
  'variantB',
  'on',
  'off',
  'enabled',
  // TODO: remove once depop_shipping_australia is rolled out to all users
  'manual_shipping_optional',
  'depop_shipping_only',
  'depop_shipping_onboarding',
] as const;

export type OpzlyExperimentConfigOption =
  (typeof OPTIMIZELY_EXPERIMENT_OPTIONS)[number];

export type OpzlyExperimentConfig = {
  [OPTIMIZELY_EXPERIMENT_CONTROL]: string;
  metadata?: {
    tags?: string[]; // Tags can be used to group or filter experiments.
    public_alias?: string; // a public alias for the experiment, e.g. for query params.
  };
  variables?: ZodMiniObject;
} & {
  [k in OpzlyExperimentConfigOption]?: string | boolean;
};

type ExperimentConfig<T extends OpzlyExperimentName> = OpzlyExperiments[T];

type VariantKeys<T extends OpzlyExperimentName> = Exclude<
  keyof ExperimentConfig<T>,
  'variables' | 'metadata'
>;

export type OpzlyExperimentValue<T extends OpzlyExperimentName> =
  ExperimentConfig<T>[VariantKeys<T>];

export type OpzlyExperimentVariables<T extends OpzlyExperimentsWithVariables> =
  z.infer<ExperimentConfig<T>['variables']>;

export type HasVariable<T extends OpzlyExperimentName> = T extends T
  ? ExperimentConfig<T> extends { variables: ZodMiniObject }
    ? T
    : never
  : never;

export type OpzlyExperimentsWithVariables = HasVariable<OpzlyExperimentName>;

/**
 * Arbitrary user attributes sent on the Depop-Additional-Attribute header of the
 * bucketing call and forwarded to Optimizely, e.g. `{ seller_tier: 'gold' }`.
 * Keys and values must not contain a comma — see buildAdditionalAttributeHeader.
 */
export type AdditionalAttributes = Record<string, string | number | boolean>;
